import { API_BASE_URL } from '../config';

// Function to check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const { exp } = JSON.parse(jsonPayload);
    return exp * 1000 < Date.now();
  } catch (e) {
    console.error('Error parsing token:', e);
    return true;
  }
};

// Global request tracking for showing a loading spinner across the app
let __activeApiRequests = 0;
const notifyActiveRequests = () => {
  try {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('api:active', { detail: __activeApiRequests }));
    }
  } catch (_) {
    // no-op
  }
};

// Base fetch function that always includes the bearer token
export const fetchWithAuth = async (endpoint, options = {}) => {
  const token = localStorage.getItem('jwtToken');
  if (!token) {
    throw new Error('Not authenticated');
  }
  if (isTokenExpired(token)) {
    localStorage.removeItem('jwtToken');
    localStorage.setItem('loginMessage', 'Your session has expired. Please log in again.');
    window.location.href = '/on/login';
    throw new Error('Session expired. Please log in again.');
  }
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers
  };
  __activeApiRequests += 1;
  notifyActiveRequests();
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
      mode: 'cors'
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.statusText}`);
    }
    if (options.method === 'DELETE') {
      return response;
    }
    const acceptHeader = headers['Accept'];
    if (acceptHeader === 'text/html') {
      return response.text();
    } else if (acceptHeader === 'application/pdf' || acceptHeader === 'image/*') {
      return response;
    } else {
      return response.json();
    }
  } finally {
    __activeApiRequests = Math.max(0, __activeApiRequests - 1);
    notifyActiveRequests();
  }
};

export const fetchWithCorpId = async (endpoint, options = {}) => {
  const corpId = localStorage.getItem('selectedCorpId') || '0';
  return fetchWithAuth(endpoint, {
    ...options,
    headers: {
      'X-Corp-ID': corpId,
      ...options.headers
    }
  });
};

export const fetchWithCorpIdAndFormData = async (endpoint, formData, options = {}) => {
  const corpId = localStorage.getItem('selectedCorpId') || '0';
  const token = localStorage.getItem('jwtToken');
  if (!token) {
    throw new Error('Not authenticated');
  }
  if (isTokenExpired(token)) {
    localStorage.removeItem('jwtToken');
    localStorage.setItem('loginMessage', 'Your session has expired. Please log in again.');
    window.location.href = '/on/login';
    throw new Error('Session expired. Please log in again.');
  }
  const headers = {
    'X-Corp-ID': corpId,
    'Authorization': `Bearer ${token}`,
    ...options.headers
  };
  __activeApiRequests += 1;
  notifyActiveRequests();
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      body: formData,
      credentials: 'include',
      mode: 'cors'
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.statusText}`);
    }
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return response.json();
    } else {
      return response.text();
    }
  } finally {
    __activeApiRequests = Math.max(0, __activeApiRequests - 1);
    notifyActiveRequests();
  }
};